import { CommonWidgetProps } from "@/widget-components/types";
import { useFirehosePollingHeader } from "@/shared/api/firehose/useFirehosePollingHeader";
import { useStocksNewsFeed } from "./hooks/useStocksNewsFeed";
import { FirehoseHighlightItem } from "../firehose-all/components";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { useFirehoseAdvancedSearchPanel } from "../shared/hooks/useFirehoseAdvancedSearchPanel";
import { BackendSource } from "@/shared/api/firehose";
import FilterHighlightPanels from "@/ui-components/FilterPanel/FilterHighlightPanels";
import { VirtuosoWrapper } from "@/ui-components/grouped-virtuoso";

const storyType = [
  "stocks-one",
  "stocks-two",
  "stocks-three",
  "stocks-four",
  "stocks-four-filings",
] as BackendSource[];

function StocksNews(props: CommonWidgetProps) {
  useFirehosePollingHeader(props.headerOptions);
  const {
    stocksNewsStories,
    isLoading: isFeedLoading,
    loadNext: loadNextFeed,
    isLoadingMore: isLoadingMoreFeed,
    hasMore: hasMoreFeed,
  } = useStocksNewsFeed();

  const {
    filteredData: filteredStories,
    showSearchResults,
    loadNextPage: loadNextSearch,
    hasMore: hasMoreSearch,
    isSearching,
    // Filter panel
    filterSelections,
    setFilterSelections,
    isPanelOpen,
    closePanel,
    filterMenus,
    clearFilterSelections,
    // Highlight panel
    highlightSelections,
    setHighlightSelections,
    isHighlightPanelOpen,
    closeHighlightPanel,
    highlightMenus,
    clearHighlightSelections,
    highlightOrder,
    moveHighlight,
  } = useFirehoseAdvancedSearchPanel({
    storyType: storyType,
    originalData: stocksNewsStories,
    setTitle: props.headerOptions?.setTitle,
    defaultTitle: "Equities News",
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Use search load functions if search is active, otherwise use feed load functions
  const loadNext = showSearchResults ? loadNextSearch : loadNextFeed;
  const isLoadingMore = showSearchResults ? isSearching : isLoadingMoreFeed;
  const hasMore = showSearchResults ? hasMoreSearch : hasMoreFeed;
  const isLoading = showSearchResults ? isSearching : isFeedLoading;

  return (
    <div className="h-full flex flex-col">
      {/* Filter Panel - full-screen library browser modal */}
      <FilterHighlightPanels
        filterMenus={filterMenus}
        filterSelections={filterSelections}
        setFilterSelections={setFilterSelections}
        isFilterOpen={isPanelOpen}
        onClearFilter={clearFilterSelections}
        onCloseFilter={closePanel}
        highlightMenus={highlightMenus}
        highlightSelections={highlightSelections}
        setHighlightSelections={setHighlightSelections}
        isHighlightOpen={isHighlightPanelOpen}
        onClearHighlight={clearHighlightSelections}
        onCloseHighlight={closeHighlightPanel}
        highlightOrder={highlightOrder}
        onMoveHighlight={moveHighlight}
      />

      <VirtuosoWrapper
        data={filteredStories}
        scrollManager={{
          enabled: true,
          compareFirstItem: (a, b) => a?.[0]?.id === b?.[0]?.id,
        }}
        pagination={{
          hasMore,
          isLoadingMore,
          loadNext,
        }}
        renderItem={([data, highlightInfo]) => (
          <FirehoseHighlightItem
            item={data}
            highlightInfo={highlightInfo}
          />
        )}
        renderEmpty={(isLoadingEmpty) => (
          <EmptyPlaceholder
            message="No stocks news available"
            isLoading={isLoadingEmpty || isLoading}
          />
        )}
      />
    </div>
  );
}

export default StocksNews;
